import {Component, Plugins, Expressions} from '@packages/systems/wfdl';
import {
  getGuidNullable,
  type NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {
  getPageComponentFromComponentMap,
  pageComponentType,
} from './PageComponent';

import {Expression, type ExpressionElement} from '@packages/systems/wfdl-ast';
import {getComponentElementById} from './ComponentElementUtils';

/**
 * Returns the ExpressionElement representing the current (focused) components
 */
export const getCurrentComponent = (
  focusedSymbolNativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): ExpressionElement | null => {
  const currentComponentType = getCurrentComponentType(
    componentMap,
    focusedSymbolNativeIdPath
  );
  const focusedComponent = componentMap.get(currentComponentType);
  if (!focusedComponent) {
    return null;
  }
  const focusedComponentRoot = Component.getRender(focusedComponent);
  return Expressions.getElement(focusedComponentRoot);
};

/**
 * Allows callers to resolve legacy focus segments that have not been reified
 * into the component's render or defaultData. The lookup is injected because
 * this package cannot depend on `component-slots` without a dependency cycle.
 */
export type MissedFocusSegmentResolver = (
  ownerComponentType: Plugins.QualifiedElement,
  elementId: string
) => Plugins.QualifiedElement | null;

/**
 * Returns the ID of the currently focused component in the Designer.
 * The current page is considered a component and will be returned if
 * no element is focused.
 */
export const getCurrentComponentType = (
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: Array<NativeId>,
  resolveMissedSegment?: MissedFocusSegmentResolver
): Plugins.QualifiedElement => {
  if (focusedSymbolNativeIdPath.length) {
    let parent = getPageComponentFromComponentMap(componentMap);
    let parentType: Plugins.QualifiedElement = pageComponentType;
    let selectedType: Plugins.QualifiedElement | null = null;

    for (const symbolNativeId of focusedSymbolNativeIdPath) {
      const symbolId = getGuidNullable(symbolNativeId);
      if (!parent || !symbolId) {
        throw new Error(
          'Component not found from `focusedSymbolNativeIdPath`, every component in that path should exist in the componentMap'
        );
      }
      selectedType =
        getComponentElementById(parent, symbolId)?.type ??
        resolveMissedSegment?.(parentType, symbolId) ??
        null;
      if (selectedType == null) break;
      parent = componentMap.get(selectedType);
      parentType = selectedType;
    }

    return selectedType ?? pageComponentType;
  }
  return pageComponentType;
};

/**
 * Returns the currently focused component's render expression.
 */
export const getCurrentComponentRender = (
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: string[],
  resolveMissedSegment?: MissedFocusSegmentResolver
): Expression | null => {
  const currentComponentType = getCurrentComponentType(
    componentMap,
    focusedSymbolNativeIdPath,
    resolveMissedSegment
  );
  const currentComponent = componentMap.get(currentComponentType);

  return currentComponent ? Component.getRender(currentComponent) : null;
};
